/**
 * Turns the agent's streamed plain text into the small, injection-safe HTML the
 * chat transcript renders. Two layers:
 *
 *   - `formatInline` — span-level markup applied to a single run of text:
 *     `code`, **bold**, *italics*, plus URL autolinking. Everything is escaped
 *     first so streamed text can never inject markup, even mid-stream.
 *   - `formatMessage` — block-level structure (headings, ordered/unordered
 *     lists, blockquotes, fenced code blocks, GFM tables, paragraphs) built on
 *     top of `formatInline` for the content of each block.
 *
 * Autolinking exists so ordinary links in a reply become clickable. The long
 * S3 presigned URLs `doc_share_url` returns are the exception: they're surfaced
 * as a document card, so they're stripped here (along with any now-dangling
 * "here's your share link:" lead-in) rather than linked.
 *
 * Lives in its own module (not ChatPanel.tsx) so the component file only
 * exports a component — keeps React Fast Refresh happy and makes these pure
 * functions trivially unit-testable.
 */

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function formatInline(text: string): string {
  const escaped = escapeHtml(text);
  // Presigned share URLs are surfaced as a document card, not a raw link — drop
  // them from the reply, and (only when one was actually removed) tidy a now-
  // dangling lead-in like "… and here's your share link:" left behind.
  //
  // Match on the S3 host (`amazonaws.com`) or ANY `X-Amz-` query param rather
  // than waiting for `X-Amz-Signature` specifically: those markers appear at the
  // very start of the URL, so a presigned link is stripped the moment it begins
  // streaming instead of slowly typing out its full ~250-char length before the
  // trailing signature finally arrives and triggers the strip.
  let body = escaped.replace(/https?:\/\/[^\s<]{0,2048}(?:amazonaws\.com|X-Amz-)[^\s<]{0,2048}/gi, '');
  if (body !== escaped) {
    body = body
      .replace(
        /(?:share|download)?\s{0,20}link\s{0,20}(?:is|:|：)?\s{0,20}$/i,
        '',
      )
      .replace(/(?:here['’]s|here\s{1,10}is)?\s{0,20}(?:your|the)?\s{0,20}$/i, '')
      .replace(/[\s,]{0,20}(?:and\s{0,10})?$/i, '')
      .replace(/[\s,:：]{1,40}$/, '');
  }
  // Pull explicit [label](url) links out first so the bare-URL autolinker below
  // can't re-wrap the URL sitting inside the href we just built. Only http(s)
  // targets are accepted, so a `javascript:`/`data:` URI can never form a link.
  // Restored after the inline passes so those can't mangle the href either.
  const links: string[] = [];
  body = body.replace(
    /\[([^\]\n]{1,500})\]\((https?:\/\/[^)\s]{1,2000})\)/g,
    (_m: string, label: string, url: string) => {
    const href = url.replace(/"/g, '&quot;');
    links.push(`<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`);
    return `L${links.length - 1}`;
  });
  return body
    .replace(/https?:\/\/[^\s<]{1,2000}/g, (match) => {
      // Don't swallow trailing prose punctuation into the link.
      const trail = /[.,!?)\]'"]{1,20}$/.exec(match)?.[0] ?? '';
      const url = trail ? match.slice(0, match.length - trail.length) : match;
      // `&` is already `&amp;`; also neutralize any stray quote so it can't
      // break out of the href attribute.
      const href = url.replace(/"/g, '&quot;');
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>${trail}`;
    })
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Italics: a single-asterisk pair, applied after bold so `**x**` is already
    // consumed. Line-anchored list markers are stripped before this runs. Only
    // `*` is treated as emphasis (not `_`) so snake_case tool names like
    // `recall_previous_conversation` survive intact.
    .replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
    .replace(/~~([^~]+)~~/g, '<del>$1</del>')
    .replace(/L(\d+)/g, (_m, n) => links[Number(n)]);
}

/** A numbered-list item: a standard `1.` / `1)` marker. Headings (`#`+) are
 *  handled separately, matching how an LLM emits markdown. */
function isOrderedItem(line: string): boolean {
  return /^\s*\d+[.)]\s+\S/.test(line);
}

function orderedContent(line: string): string {
  return line.replace(/^\s*\d+[.)]\s+/, '');
}

/** Leading-whitespace width of a line, a tab counted as four columns. */
function indentWidth(line: string): number {
  const lead = /^[ \t]{0,200}/.exec(line)?.[0] ?? '';
  return lead.replace(/\t/g, '    ').length;
}

/**
 * Index of the next line with content, or -1 if only blanks remain.
 *
 * Used to keep a "loose" list — one with blank lines between its items —
 * together as a single list. Models emit that form constantly, and collecting
 * only CONSECUTIVE item lines split it into one list per item. Since every
 * list restarts its own counter, a numbered list then rendered as "1. 1. 1."
 * in the chat stream.
 *
 * A blank line continues a list; anything else ends it, so genuinely separate
 * lists divided by prose still render separately.
 */
function nextContentIndex(lines: readonly string[], from: number): number {
  for (let i = from; i < lines.length; i += 1) {
    if ((lines[i] ?? '').trim() !== '') {
      return i;
    }
  }
  return -1;
}

/**
 * The lines that belong to the item just opened: everything indented deeper
 * than the list's own marker (sub-lists, wrapped prose, an indented fence),
 * with interior blank lines preserved so the recursive render still sees the
 * loose form. Stops at the first content line back at the list's indent.
 */
function collectChildLines(
  lines: readonly string[],
  from: number,
  baseIndent: number,
): { lines: readonly string[]; next: number } {
  const child: string[] = [];
  let i = from;
  let blanks = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (line.trim() === '') {
      blanks += 1;
      i += 1;
      continue;
    }
    if (indentWidth(line) <= baseIndent) {
      break;
    }
    // Only blanks that turned out to sit BETWEEN two child lines are kept.
    for (let b = 0; b < blanks; b += 1) {
      child.push('');
    }
    blanks = 0;
    child.push(line);
    i += 1;
  }
  return { lines: child, next: i };
}

/** Strip the shallowest indent shared by a child block so it renders as its own
 *  top-level markdown. */
function dedent(lines: readonly string[]): string {
  const widths = lines.filter((line) => line.trim() !== '').map(indentWidth);
  const cut = widths.length > 0 ? Math.min(...widths) : 0;
  return lines
    .map((line) =>
      line.trim() === ''
        ? ''
        : line.replace(/^[ \t]{0,200}/, (lead) => lead.replace(/\t/g, '    ').slice(cut)),
    )
    .join('\n');
}

/**
 * Collect one list's items starting at `start`, treating blank lines BETWEEN
 * items as part of the same list, and lines indented UNDER an item as that
 * item's own nested content.
 *
 * The nesting half is the second half of the same "1. 1. 1." defect: models
 * hang `-` sub-bullets under each numbered item, and ending the list at the
 * first non-item line gave every item its own <ol> — each restarting at 1. The
 * child block is rendered by `formatMessage` (mutual recursion, so a sub-list
 * nests to any depth) and placed inside the parent <li>.
 *
 * Shared by the ordered and unordered branches — they differ only in how an
 * item is recognized and how its content is extracted.
 */
function collectListItems(
  lines: readonly string[],
  start: number,
  isItem: (line: string) => boolean,
  contentOf: (line: string) => string,
): { items: readonly string[]; next: number } {
  const baseIndent = indentWidth(lines[start] ?? '');
  const items: string[] = [];
  let i = start;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    // A marker at (or left of) the list's own indent opens the next item; a
    // deeper one is child content, collected below.
    if (isItem(line) && indentWidth(line) <= baseIndent) {
      const child = collectChildLines(lines, i + 1, baseIndent);
      const nested = child.lines.length > 0 ? formatMessage(dedent(child.lines)) : '';
      items.push(`<li>${formatInline(contentOf(line))}${nested}</li>`);
      i = child.next;
      continue;
    }
    const next = nextContentIndex(lines, i);
    const candidate = lines[next] ?? '';
    if (next === -1 || !isItem(candidate) || indentWidth(candidate) > baseIndent) {
      break;
    }
    i = next;
  }
  return { items, next: i };
}

/** A bullet item: `-`, `+`, or `*` followed by whitespace (the trailing space
 *  is what separates a `* item` bullet from an inline `*italic*`). */
function isBulletItem(line: string): boolean {
  return /^\s*[-+*]\s+\S/.test(line);
}

function bulletContent(line: string): string {
  return line.replace(/^\s*[-+*]\s+/, '');
}

/** Split a GFM table row into trimmed cells, tolerating optional edge pipes. */
function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) {
    trimmed = trimmed.slice(1);
  }
  if (trimmed.endsWith('|')) {
    trimmed = trimmed.slice(0, -1);
  }
  return trimmed.split('|').map((cell) => cell.trim());
}

/** The `| --- | :--: |` row directly under a table header. */
function isTableDelimiter(line: string): boolean {
  if (!line.includes('-')) {
    return false;
  }
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell));
}

/** True for the start of a table: a header row whose next line is the delimiter. */
function isTableStart(lines: string[], i: number): boolean {
  return lines[i].includes('|') && i + 1 < lines.length && isTableDelimiter(lines[i + 1]);
}

/** Any line that begins a non-paragraph block — used to break paragraph runs. */
function isBlockStart(lines: string[], i: number): boolean {
  const line = lines[i];
  return (
    /^\s*```/.test(line) ||
    /^#{1,6}\s+/.test(line) ||
    /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line) ||
    /^\s*>\s?/.test(line) ||
    isOrderedItem(line) ||
    isBulletItem(line) ||
    isTableStart(lines, i)
  );
}

function renderTable(header: string[], rows: string[][]): string {
  const head = header.map((cell) => `<th>${formatInline(cell)}</th>`).join('');
  const body = rows
    .map((row) => {
      const cells = header.map((_, col) => `<td>${formatInline(row[col] ?? '')}</td>`).join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');
  return `<div class="om-md-table-wrap"><table class="om-md-table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
}

/**
 * Render a full message as block-level HTML. `streaming` appends the blinking
 * cursor — tucked inside the trailing paragraph when there is one so it sits at
 * the end of the text rather than dropping onto its own line.
 */
/**
 * When a reply shares a document, the presigned URL is surfaced as a
 * DocumentCard, so the URL is stripped from the prose (see `formatInline`). But
 * that leaves dangling boilerplate around where the URL was — "Here's your
 * download link for X:" and "The link expires in 1 hour." — often in their own
 * paragraphs, which the per-block strip can't reach. When a presigned link is
 * present, remove that whole boilerplate at the message level so the card is the
 * only thing shown. Only fires when a presigned/S3 URL is actually present, so
 * ordinary replies are untouched.
 */
function stripShareBoilerplate(text: string): string {
  if (!/https?:\/\/[^\s<]*(?:amazonaws\.com|X-Amz-)/i.test(text)) {
    return text;
  }
  return text
    .replace(/https?:\/\/[^\s<]{0,2048}(?:amazonaws\.com|X-Amz-)[^\s<]{0,2048}/gi, '')
    .replace(
      /(?:here['’]s|here\s{1,10}is)\s{1,20}(?:your|the)\s{0,20}(?:download|share)?\s{0,20}link(?:\s{1,10}for\s{1,10}[^\n:]{0,200})?\s{0,20}[:：]?/gi,
      '',
    )
    .replace(/(?:the|this)\s{1,10}(?:download\s{1,10}|share\s{1,10})?link\s{1,10}expires?\s{1,10}in[^\n.]{0,300}\.?/gi, '')
    .replace(/[ \t]{1,400}\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function formatMessage(text: string, opts: { streaming?: boolean } = {}): string {
  const lines = stripShareBoilerplate(text).split('\n');
  const blocks: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block — verbatim, escaped, no inline markup inside.
    if (/^\s*```/.test(line)) {
      i += 1;
      const code: string[] = [];
      while (i < lines.length && !/^\s*```/.test(lines[i])) {
        code.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) {
        i += 1; // consume the closing fence
      }
      blocks.push(`<pre class="om-md-pre"><code>${escapeHtml(code.join('\n'))}</code></pre>`);
      continue;
    }

    // Blank line — paragraph separator, nothing to emit.
    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // GFM table — header row + delimiter row + body rows.
    if (isTableStart(lines, i)) {
      const header = splitTableRow(line);
      i += 2; // skip header + delimiter
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(splitTableRow(lines[i]));
        i += 1;
      }
      blocks.push(renderTable(header, rows));
      continue;
    }

    // Horizontal rule — a line of only `---`, `***`, or `___` (3+).
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push('<hr class="om-md-hr" />');
      i += 1;
      continue;
    }

    // Heading — `#` through `######`, matching standard markdown / LLM output.
    const heading = /^(#{1,6})[ \t]{1,20}(.{0,500})$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      blocks.push(`<h${level} class="om-md-h">${formatInline(heading[2])}</h${level}>`);
      i += 1;
      continue;
    }

    // Blockquote — consecutive `>` lines joined into one quote.
    if (/^\s*>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push(`<blockquote class="om-md-quote">${formatInline(quote.join(' '))}</blockquote>`);
      continue;
    }

    // Ordered list — `1.` / `1)` markers.
    if (isOrderedItem(line)) {
      const list = collectListItems(lines, i, isOrderedItem, orderedContent);
      i = list.next;
      blocks.push(`<ol class="om-md-ol">${list.items.join('')}</ol>`);
      continue;
    }

    // Unordered list — `-`, `+`, or `* ` markers.
    if (isBulletItem(line)) {
      const list = collectListItems(lines, i, isBulletItem, bulletContent);
      i = list.next;
      blocks.push(`<ul class="om-md-ul">${list.items.join('')}</ul>`);
      continue;
    }

    // Paragraph — gather plain lines until a blank line or a new block.
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !isBlockStart(lines, i)) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(`<p>${formatInline(para.join(' '))}</p>`);
  }

  let html = blocks.join('');
  if (opts.streaming) {
    const cursor = '<span class="om-cursor"></span>';
    // Keep the cursor inline at the end of a trailing paragraph; otherwise (a
    // list/heading/table just closed, or no content yet) drop it after.
    html = html.endsWith('</p>') ? `${html.slice(0, -4)}${cursor}</p>` : `${html}${cursor}`;
  }
  return html;
}
